import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, ScrollView } from 'react-native';
import LHOverlay from '../../../components/LHOverlay';
import FontFamily from '../../../constants/fonts';
import { createApiClient, apiClient } from '../../../api/apiClient';
import { formatUserName } from '../../../utils/emailTouserName';
import { handleStandUpErrorWithFriendlyMessage } from '../../../store/utils/standUpErrorHandler';
import { STANDUP_API_NAMES } from '../../../store/slices/standUpSlice';

interface NewsDetailsOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  newsId: string;
}

interface NewsDetails {
  title: string;
  description: string;
  created_at: string;
  created_by: string;
}

const NewsDetailsOverlay: React.FC<NewsDetailsOverlayProps> = ({
  visible,
  onDismiss,
  newsId,
}) => {
  const [newsDetails, setNewsDetails] = useState<NewsDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch news details when overlay opens
  useEffect(() => {
    if (visible && newsId) {
      fetchNewsDetails();
    }
  }, [visible, newsId]);

  const fetchNewsDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const api = createApiClient();
      const response = await apiClient(
        api,
        `/stand-up/get-news-details?news_id=${newsId}`,
        'GET'
      );

      if (response?.success && response?.data) {
        setNewsDetails(response.data);
      } else {
        const msg = response?.message || response?.data?.message || 'Failed to load news details';
        try {
          const handled = handleStandUpErrorWithFriendlyMessage(
            STANDUP_API_NAMES.FETCH_NEWS,
            msg,
            true, // show overlay/modal to user
            { newsId }
          );
          setError(handled?.message || msg);
        } catch (handlerErr) {
          console.error('Error while invoking standup error handler for news details:', handlerErr);
          setError(msg);
        }
      }
    } catch (err: any) {
      console.error('Error fetching news details:', err);
      const originalMsg = err?.response?.data?.message || err?.message || 'Failed to load news details';
      try {
        const handled = handleStandUpErrorWithFriendlyMessage(
          STANDUP_API_NAMES.FETCH_NEWS,
          err,
          true, // show overlay/modal
          { newsId }
        );
        setError(handled?.message || originalMsg);
      } catch (handlerErr) {
        console.error('Error while invoking standup error handler for news details:', handlerErr);
        setError(originalMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Format date with timezone conversion to show correct local date
  const formatDate = (dateString: string) => {
    try {
      // Convert UTC timestamp to local timezone
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString;
      }
      
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      return `${day} ${monthNames[month - 1]} ${year}`;
    } catch (e) {
      return dateString;
    }
  };

  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      // Get local hours and minutes to convert UTC timestamp to client's local timezone
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12; // Convert to 12-hour format
      const displayMinutes = minutes.toString().padStart(2, '0');
      return `${displayHours}:${displayMinutes} ${ampm}`;
    } catch (e) {
      return '';
    }
  };

  return (
    <LHOverlay
      visible={visible}
      onDismiss={onDismiss}
      title=""
      size="medium"
      close={true}
      dismissOnClickOutside={false}
      hideFooter={true}
      backgroundColor="#FFFFFF"
      headerPaddingHorizontal={40}
      headerPaddingTop={24}
      hideHeaderDivider={true}
      bodyPadding={{ horizontal: 40, vertical: { top: 24, bottom: 24 } }}
      containerStyle={{ maxWidth: 800 }}
    >
      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#005CBC" />
            <Text style={styles.loadingText}>Loading news details...</Text>
          </View>
        ) : error ? (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        ) : newsDetails ? (
          <View style={styles.contentContainer}>
            {/* Title Section */}
            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Title:</Text>
              <Text style={styles.title}>{newsDetails.title}</Text>
            </View>

            {/* Date, Time and Created By Section */}
            <View style={styles.fieldContainer}>
              <View style={styles.dateTimeContainer}>
                <Text style={styles.date}>{formatDate(newsDetails.created_at)}</Text>
                <Text style={styles.time}> • {formatTime(newsDetails.created_at)}</Text>
                <Text style={styles.createdBy}> - Created By {formatUserName(newsDetails.created_by)}</Text>
              </View>
            </View>

            {/* Description Section */}
            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Description:</Text>
              <Text style={styles.description}>{newsDetails.description}</Text>
            </View>
          </View>
        ) : null}
      </ScrollView>
    </LHOverlay>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 14,
    color: '#616161',
    fontFamily: FontFamily.regular,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  errorText: {
    fontSize: 14,
    color: '#D32F2F',
    fontFamily: FontFamily.regular,
    textAlign: 'center',
  },
  contentContainer: {
    flex: 1,
  },
  fieldContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1C',
    fontFamily: FontFamily.bold,
    marginBottom: 8,
  },
  title: {
    fontSize: 16,
    fontWeight: '400',
    color: '#1C1C1C',
    fontFamily: FontFamily.regular,
    lineHeight: 24,
  },
  dateTimeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  date: {
    fontSize: 12,
    fontWeight: '400',
    color: '#757575',
    fontFamily: FontFamily.regular,
  },
  time: {
    fontSize: 12,
    fontWeight: '400',
    color: '#757575',
    fontFamily: FontFamily.regular,
  },
  createdBy: {
    fontSize: 12,
    fontWeight: '400',
    color: '#757575',
    fontFamily: FontFamily.regular,
  },
  description: {
    fontSize: 14,
    fontWeight: '400',
    color: '#4B4B4B',
    fontFamily: FontFamily.regular,
    lineHeight: 22,
  },
});

export default NewsDetailsOverlay;
